/**
 * Full-width mobile player card for squad planning.
 * Renders a dark card with GPR pill, stat tiles, and info badges
 * matching the Figma accordion card design.
 * @module features/squad-planning/components/MobilePlayerCard
 */

import React, { memo } from "react";
import { Box } from "@/components/atoms/Box";

import { Badge, BadgeText } from "@/components/atoms/Badge";
import { Stack } from "@/components/atoms/Stack";
import { Image } from "@/components/atoms/Image";
import { Pressable } from "@/components/atoms/Pressable";
import { Text } from "@/components/atoms/Text";

import type { PlayerAction } from "@/features/squad-planning/types";
import { formatNumber } from "@/utils/intlFormat";

/** Stable callback to claim the responder and block tap propagation to the card. */
const claimResponder = () => true;

/** Maps player actions to badge display config. */
/**
 * Action badge colours follow the SE-5344 convention shared with the
 * desktop action column: Sell red, Loan warning/orange, Sign green,
 * Promote accent blue. (SE-5355's AC sketched "green for Sign/Promote";
 * cross-surface consistency with the shipped desktop badges wins, and
 * this also retires the old #8B5CF6 arbitrary-value waiver.)
 */
const ACTION_BADGE_CONFIG: Record<
  PlayerAction,
  { label: string; badgeClass: string }
> = {
  Sign: { label: "NEW", badgeClass: "bg-status-success" },
  Loan: { label: "LOAN", badgeClass: "bg-status-warning" },
  Sell: { label: "SOLD", badgeClass: "bg-status-error" },
  Promote: { label: "PROMOTED", badgeClass: "bg-accent-primary" },
};

/** Contract months threshold for warning highlight. */
const CONTRACT_WARNING_MONTHS = 12;

/** Props for {@link MobilePlayerCardView}. */
interface MobilePlayerCardViewProps {
  /** Player display name */
  readonly displayName: string;
  /**
   * The active UI locale, for the minutes group separator (SE-7966).
   * Supplied by the Container — this View is arrow-shorthand under the
   * component-structure rule and cannot read a hook itself.
   */
  readonly locale: string;
  /** Player's current team, or null */
  readonly teamName: string | null;
  /** Primary position abbreviation for the badge */
  readonly primaryPosition: string;
  /** Player nationality, or null */
  readonly nationalityName: string | null;
  /** URL to the player's nationality flag image, or null */
  readonly nationalityFlagUrl: string | null;
  /** Short tier label (e.g. "1st", "Rot", "Acd") */
  readonly tierLabel: string | null;
  /** GPR score, or null if unavailable */
  readonly gprScore: number | null;
  /** Annual wage in EUR, or null */
  readonly wage: number | null;
  /** Minutes played, or null */
  readonly minutesPlayed: number | null;
  /** Months remaining on contract, or null */
  readonly contractMonths: number | null;
  /** Player availability status (e.g. "AVAILABLE", "INJURED") */
  readonly status: string | null;
  /** Optional scenario action for badge display */
  readonly action?: PlayerAction | null;
  /** Optional slot for the ActionMenu rendered in the header row */
  readonly actionSlot?: React.ReactNode;
  /** Callback when the card is pressed */
  readonly onPress: () => void;
}

/**
 * Formats a wage number into a short currency string.
 * @param wage - Raw wage value in EUR
 * @returns Formatted string like "EUR5.2M" or "EUR50K", or em-dash if null
 */
function formatWage(wage: number | null): string {
  if (wage == null) return "\u2014";
  if (wage >= 1_000_000)
    return `\u20AC${parseFloat((wage / 1_000_000).toFixed(1))}M`;
  if (wage >= 1_000) return `\u20AC${parseFloat((wage / 1_000).toFixed(0))}K`;
  return `\u20AC${wage}`;
}

/**
 * Formats contract months into a display string.
 * @param months - Months remaining, or null
 * @returns Formatted string like "18 mo" or em-dash
 */
function formatContractMonths(months: number | null): string {
  if (months == null) return "\u2014";
  return `${months} mo`;
}

/** Status badge class map keyed by API status value. */
const STATUS_CLASSES: Readonly<
  Record<string, { bg: string; text: string; border: string }>
> = {
  AVAILABLE: {
    bg: "bg-needs-semantic-success-50",
    text: "text-status-success-strong",
    border: "border-needs-semantic-success-300",
  },
  INJURED: {
    bg: "bg-needs-semantic-error-50",
    text: "text-needs-semantic-error-700",
    border: "border-needs-semantic-error-300",
  },
};

/** Status to short display label. */
const STATUS_LABELS: Readonly<Record<string, string>> = {
  AVAILABLE: "Avail.",
  INJURED: "Injured",
};

/** Contract tile class sets for normal vs warning state. */
const CONTRACT_TILE_NORMAL =
  "border-outline-default bg-surface-subtle" as const;
const CONTRACT_TILE_WARNING =
  "border-status-warning bg-status-warning-surface" as const;

/**
 * Stateless mobile player card matching the Figma accordion design.
 * Dark card with GPR pill, 3 stat tiles, and bottom info badges.
 * @param props - View props with pre-resolved display values
 * @param props.displayName - Player name
 * @param props.locale - The active UI locale, for the minutes group separator
 * @param props.teamName - Team name or null
 * @param props.primaryPosition - Position abbreviation for badge
 * @param props.nationalityName - Nationality or null
 * @param props.nationalityFlagUrl - URL to nationality flag image or null
 * @param props.tierLabel - Short tier label or null
 * @param props.gprScore - GPR score or null
 * @param props.wage - Wage or null
 * @param props.minutesPlayed - Minutes played or null
 * @param props.contractMonths - Months remaining on contract or null
 * @param props.status - Player availability status or null
 * @param props.action - Scenario action for badge or null
 * @param props.actionSlot - Optional ActionMenu slot for scenario mode
 * @param props.onPress - Press handler
 * @returns The mobile player card JSX
 */
const MobilePlayerCardView = ({
  displayName,
  locale,
  teamName,
  primaryPosition,
  nationalityName,
  nationalityFlagUrl,
  tierLabel,
  gprScore,
  wage,
  minutesPlayed,
  contractMonths,
  status,
  action = null,
  actionSlot,
  onPress,
}: MobilePlayerCardViewProps) => (
  <Stack className="px-4 pb-3">
    <Pressable onPress={onPress} testID="mobile-player-card">
      <Stack
        className={`gap-3 rounded-md border border-outline-default bg-surface-base px-4 pb-4 pt-3${
          action != null ? " border-l-4 border-l-accent-primary" : ""
        }`}
      >
        {/* Row 1: Name + Club | GPR pill */}
        <Stack direction="horizontal" className="items-center justify-between">
          <Stack className="min-w-0 flex-1">
            <Stack direction="horizontal" className="items-center gap-2">
              <Text
                variant="title-sm"
                className="min-w-0 flex-1 text-content-primary"
                numberOfLines={1}
              >
                {displayName}
              </Text>
              {actionSlot != null ? (
                <Box
                  {...({ onStartShouldSetResponder: claimResponder } as object)}
                >
                  {actionSlot}
                </Box>
              ) : (
                action != null && (
                  <Badge
                    className={`${ACTION_BADGE_CONFIG[action].badgeClass} rounded-none px-1 py-micro`}
                  >
                    <BadgeText className="font-heading text-micro text-content-on-accent">
                      {ACTION_BADGE_CONFIG[action].label}
                    </BadgeText>
                  </Badge>
                )
              )}
            </Stack>
            {teamName != null && (
              <Text variant="micro" className="text-content-subtle">
                {teamName}
              </Text>
            )}
          </Stack>
          {gprScore != null && (
            // GPR pill — data-viz brand colors tokenized in the chart annex
            // (chart-gpr fill / chart-gprStrong readout, tailwind.config.js).
            <Box className="h-8 w-10 items-center justify-center rounded-full bg-chart-gpr">
              <Text variant="body" className="text-chart-gprStrong">
                {Math.round(gprScore)}
              </Text>
            </Box>
          )}
        </Stack>

        {/* Row 2: Stat tiles — Mins | Contract | Wage */}
        <Stack direction="horizontal" className="gap-2">
          <Stack className="flex-1 items-center justify-center rounded-sm border border-outline-default bg-surface-raised p-2">
            <Text variant="title-sm" className="text-content-primary">
              {minutesPlayed != null
                ? formatNumber(minutesPlayed, locale)
                : "\u2014"}
            </Text>
            <Text variant="caption" className="text-content-subtle">
              Mins
            </Text>
          </Stack>
          <Stack
            className={`flex-1 items-center justify-center rounded-sm border p-2 ${
              contractMonths != null &&
              contractMonths <= CONTRACT_WARNING_MONTHS
                ? CONTRACT_TILE_WARNING
                : CONTRACT_TILE_NORMAL
            }`}
          >
            <Text
              variant="title-sm"
              className={`${
                contractMonths != null &&
                contractMonths <= CONTRACT_WARNING_MONTHS
                  ? "text-status-warning"
                  : "text-content-primary"
              }`}
            >
              {formatContractMonths(contractMonths)}
            </Text>
            <Text variant="caption" className="text-content-subtle">
              Contract
            </Text>
          </Stack>
          <Stack className="flex-1 items-center justify-center rounded-sm border border-outline-default bg-surface-raised p-2">
            <Text variant="title-sm" className="text-content-primary">
              {formatWage(wage)}
            </Text>
            <Text variant="caption" className="text-content-subtle">
              Wage
            </Text>
          </Stack>
        </Stack>

        {/* Row 3: Badges — Position | Flag + Nationality | Tier | Status */}
        <Stack
          direction="horizontal"
          className="flex-wrap items-center justify-between"
        >
          <Stack direction="horizontal" className="items-center gap-1">
            <Badge className="rounded-none bg-surface-muted px-2 py-1">
              <BadgeText className="text-caption text-content-secondary">
                {primaryPosition}
              </BadgeText>
            </Badge>
            {nationalityFlagUrl != null && (
              <Image
                source={{ uri: nationalityFlagUrl }}
                className="h-3 w-5"
                alt={`${nationalityName ?? ""} flag`}
              />
            )}
            {nationalityName != null && (
              <Text variant="body" className="text-content-muted">
                {nationalityName}
              </Text>
            )}
            {tierLabel != null && (
              <Badge className="rounded-none bg-status-info-surface px-2 py-1">
                <BadgeText className="text-caption text-status-info">
                  {tierLabel}
                </BadgeText>
              </Badge>
            )}
          </Stack>
          {status != null && STATUS_LABELS[status] != null && (
            <Badge
              className={`rounded-none border px-2 py-1 ${STATUS_CLASSES[status]?.bg} ${STATUS_CLASSES[status]?.border}`}
            >
              <BadgeText
                className={`text-caption normal-case ${STATUS_CLASSES[status]?.text}`}
              >
                {STATUS_LABELS[status]}
              </BadgeText>
            </Badge>
          )}
        </Stack>
      </Stack>
    </Pressable>
  </Stack>
);

MobilePlayerCardView.displayName = "MobilePlayerCardView";

export default memo(MobilePlayerCardView);
